// src/components/onboarding/WelcomeSetupModal.tsx
// ─────────────────────────────────────────────────────────────────────────────
// First-login workspace setup: interface language + org-wide AI + data region.
//
// DESIGN (v4 — 2026-09-08):
//   • DECOUPLED from the tutorial. This modal used to be step 0 of the tutorial
//     overlay, which meant dismissing it dropped the user straight into a
//     16-step walkthrough they had not asked for, and picking a language
//     auto-advanced past the AI and region controls before they could be seen.
//   • Now it stands alone: choose, confirm, done. The tutorial is launched
//     deliberately from the dashboard ("Start Tutorial") or the user menu.
//   • Shown once. Dismissal is recorded by the tutorial context so it does not
//     reappear on every page load.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ArrowForward as NextIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import { useTutorial } from "hooks/useOnboardingTutorial";
import { useLegalCompliance } from "hooks/useLegalCompliance";
import {
  useTranslation,
  useLanguageSwitcher,
  type SupportedLanguage,
} from "../../i18n";
import { SUPPORTED_LANGUAGES } from "../../i18n/types";
import { athenaColors } from "../../theme/palette";
import { athenaDarkSectionSx } from "../layout/AthenaDialogStyles";
import {
  Z_CARD,
  Z_POPOVER,
  onboardingBackdropSx,
  onboardingCardSx,
  onboardingFieldSx,
} from "./onboardingChrome";
import api from "../../api/api";

interface OrgSettings {
  ai_enabled: boolean;
  ai_available: boolean;
  data_processing_region: "eu" | "global";
}

const WelcomeSetupModal: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isWelcomeOpen, dismissWelcome } = useTutorial();
  const { hasPendingLegal } = useLegalCompliance();
  const { language, setLanguage } = useLanguageSwitcher();

  const [orgSettings, setOrgSettings] = useState<OrgSettings | null>(null);
  const loadedRef = useRef(false);

  const loadOrgSettings = useCallback(async () => {
    try {
      const res = await api.get("/billing/organization/");
      setOrgSettings({
        ai_enabled: !!res.data?.ai_enabled,
        ai_available: res.data?.ai_available !== false,
        data_processing_region:
          res.data?.data_processing_region === "global" ? "global" : "eu",
      });
    } catch {
      loadedRef.current = false;
    }
  }, []);

  const persistOrgSetting = useCallback(
    async (patch: {
      ai_enabled?: boolean;
      data_processing_region?: "eu" | "global";
    }) => {
      // Optimistic update, then persist via the shared org-settings endpoint.
      setOrgSettings((prev) => (prev ? { ...prev, ...patch } : prev));
      try {
        await api.patch("/billing/organization/settings/", patch);
      } catch {
        // On failure, refetch to restore the true server state.
        loadOrgSettings();
      }
    },
    [loadOrgSettings]
  );

  // Fetch lazily, only once the modal is actually open, so the extra
  // /billing/organization/ call is not made on every page load.
  useEffect(() => {
    if (!isWelcomeOpen || hasPendingLegal || loadedRef.current) return;
    loadedRef.current = true;
    loadOrgSettings();
  }, [isWelcomeOpen, hasPendingLegal, loadOrgSettings]);

  // Legal acceptance takes precedence over workspace setup.
  if (!isWelcomeOpen || hasPendingLegal) return null;

  // The region preference only selects the Azure deployment for LLM calls (see
  // core/llm/deployment.py), so it is genuinely meaningless while AI is off.
  // Keeping it disabled is right; leaving that UNEXPLAINED is not, because the
  // control then just swallows clicks with no visible reason.
  const regionDisabled = !orgSettings || !orgSettings.ai_enabled;
  // Disabled for two different reasons, but only ONE of them is the user's to
  // act on. While the workspace is still loading, telling them to "enable AI"
  // would be wrong, so the hint is limited to the AI-is-off case.
  const regionBlockedByAi = !!orgSettings && !orgSettings.ai_enabled;

  return (
    <Box sx={onboardingBackdropSx(true)}>
      <Box
        sx={{
          ...onboardingCardSx(false),
          width: { xs: 320, sm: 480 },
          maxHeight: "90vh",
          overflowY: "auto",
          zIndex: Z_CARD,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 1, color: theme.palette.common.white }}
        >
          {t("tutorial.language.title", "Welcome to Athena")}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mb: 1.5,
            color: alpha(theme.palette.common.white, 0.8),
            lineHeight: 1.6,
          }}
        >
          {t(
            "tutorial.language.description",
            "Set your interface language and workspace defaults. You can change these later in Settings."
          )}
        </Typography>

        {/* ─── Language picker ─── */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 2.5,
            justifyContent: "center",
          }}
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isSelected = language === lang.code;
            return (
              <Chip
                key={lang.code}
                label={lang.nativeName}
                // Applies immediately, but does NOT dismiss the modal: the
                // AI and region controls below still need to be reachable.
                onClick={() => setLanguage(lang.code as SupportedLanguage)}
                variant={isSelected ? "filled" : "outlined"}
                icon={
                  <LanguageIcon
                    sx={{
                      fontSize: 14,
                      color: isSelected
                        ? theme.palette.common.white
                        : athenaColors.periwinkle,
                    }}
                  />
                }
                sx={{
                  fontWeight: isSelected ? 700 : 400,
                  fontSize: "0.8rem",
                  borderColor: isSelected
                    ? athenaColors.periwinkle
                    : alpha(athenaColors.periwinkle, 0.35),
                  backgroundColor: isSelected
                    ? alpha(athenaColors.periwinkle, 0.25)
                    : "transparent",
                  color: isSelected
                    ? theme.palette.common.white
                    : alpha(theme.palette.common.white, 0.75),
                  "&:hover": {
                    backgroundColor: alpha(athenaColors.periwinkle, 0.15),
                    borderColor: athenaColors.periwinkle,
                    color: theme.palette.common.white,
                  },
                }}
              />
            );
          })}
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 2,
            color: alpha(athenaColors.periwinkle, 0.7),
            lineHeight: 1.5,
            fontStyle: "italic",
          }}
        >
          {t(
            "tutorial.language.db_note",
            "Note: The interface is displayed in your chosen language, but clinical database fields remain in English to ensure consistency across centres and users."
          )}
        </Typography>

        {/* ─── Workspace defaults: org-wide AI + data region ─── */}
        <Box
          sx={{
            ...athenaDarkSectionSx,
            ...onboardingFieldSx,
            mb: 2.5,
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Enable AI (org-wide) */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={!!orgSettings?.ai_enabled}
                  disabled={!orgSettings || orgSettings.ai_available === false}
                  onChange={(e) =>
                    persistOrgSetting({ ai_enabled: e.target.checked })
                  }
                />
              }
              label={t("tutorial.settings.enable_ai", "Enable AI")}
            />
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: alpha(athenaColors.white, 0.7),
                lineHeight: 1.5,
              }}
            >
              {orgSettings?.ai_available === false
                ? t(
                    "tutorial.settings.ai_not_in_plan",
                    "AI is not included in your current plan."
                  )
                : t(
                    "tutorial.settings.enable_ai_desc",
                    "Run AI extraction automatically across every environment in your workspace."
                  )}
            </Typography>
          </Box>

          {/* Data Processing Region */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 0.75,
                color: athenaColors.white,
                fontWeight: 600,
              }}
            >
              {t("tutorial.settings.data_region", "Data Processing Region")}
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={orgSettings?.data_processing_region || "eu"}
                disabled={regionDisabled}
                // Without this the menu opens at MUI's modal layer (1300),
                // i.e. UNDERNEATH this modal: the user clicks, the menu
                // renders, and nothing is visible. See Z_POPOVER.
                MenuProps={{ sx: { zIndex: Z_POPOVER } }}
                onChange={(e) =>
                  persistOrgSetting({
                    data_processing_region: e.target.value as "eu" | "global",
                  })
                }
              >
                <MenuItem value="eu">
                  {t("tutorial.settings.region_eu", "Europe only")}
                </MenuItem>
                <MenuItem value="global">
                  {t("tutorial.settings.region_global", "Global (fastest)")}
                </MenuItem>
              </Select>
            </FormControl>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.75,
                color: alpha(athenaColors.white, 0.7),
                lineHeight: 1.5,
              }}
            >
              {regionBlockedByAi
                ? t(
                    "tutorial.settings.region_needs_ai",
                    "Enable AI above to choose where data is processed."
                  )
                : orgSettings?.data_processing_region === "global"
                ? t(
                    "tutorial.settings.region_global_desc",
                    "Data may be processed in any Azure region for best performance."
                  )
                : t(
                    "tutorial.settings.region_eu_desc",
                    "Data is processed exclusively in Europe. Required for strict GDPR compliance."
                  )}
            </Typography>
          </Box>
        </Box>

        {/* ─── Confirm ─── */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            variant="contained"
            onClick={dismissWelcome}
            endIcon={<NextIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              fontSize: "0.8rem",
              minWidth: 80,
              fontWeight: 600,
              backgroundColor: athenaColors.periwinkle,
              color: theme.palette.common.white,
              "& .MuiButton-endIcon, & .MuiButton-startIcon": {
                color: "inherit",
              },
              "&:hover": {
                backgroundColor: alpha(athenaColors.periwinkle, 0.85),
              },
            }}
          >
            {t("tutorial.continue", "Continue")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default WelcomeSetupModal;
